import { fontVariant, type FontContext, type TextFeatures } from "./fonts";
import type { Copyfit } from "./copyfit";
import metrics from "./font-metrics.json";
import {
  parseImportedPaintAttribute,
  type ImportedPaint,
} from "./imported-paint";
export interface Run {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  sourcePaint?: ImportedPaint;
  size?: number;
  fontFaceId?: string;
  lineHeight?: number;
  textFeatures?: TextFeatures;
  paragraphId?: string;
  spaceBefore?: number;
  spaceAfter?: number;
  tabInterval?: number;
}
export interface PositionedRun extends Run {
  x: number;
  y: number;
  width: number;
  line: number;
  fontFaceId?: string;
  fontAscent?: number;
}
/** Keep complete glyphs only for composed lines when frame ink clipping is off. */
export function textPaintRuns(node: {
  runs?: PositionedRun[];
  height: number;
  allowGlyphOverhang?: boolean;
}): PositionedRun[] {
  return (node.runs || []).filter(
    (run) =>
      run.text.length > 0 &&
      (!node.allowGlyphOverhang || run.y <= node.height + 0.5),
  );
}
type Widths = Record<string, number>;
export function fontAscent(
  family = "Inter",
  weight = 400,
  context?: FontContext,
): number {
  if (context) return fontVariant(context).runtime.ascent;
  return (
    family === "Georgia" || family === "Libre Baskerville"
      ? metrics.serif
      : weight >= 600
        ? metrics.interBold
        : metrics.inter
  ).ascent;
}
export function fontDescent(
  family = "Inter",
  weight = 400,
  context?: FontContext,
): number {
  if (context) return -fontVariant(context).face.descent;
  return -(
    family === "Georgia" || family === "Libre Baskerville"
      ? metrics.serif
      : weight >= 600
        ? metrics.interBold
        : metrics.inter
  ).descent;
}
export function measureText(
  text: string,
  size: number,
  family = "Inter",
  weight = 400,
  context?: FontContext,
  features?: TextFeatures,
): number {
  if (context)
    return fontVariant(context).runtime.measure(text, features) * size;
  if (features?.pairAdjustments?.length)
    throw new Error("Custom kerning requires a managed font snapshot");
  const m =
    family === "Georgia" || family === "Libre Baskerville"
      ? metrics.serif
      : weight >= 600
        ? metrics.interBold
        : metrics.inter;
  return [...text].reduce(
    (width, c) =>
      width + ((m.widths as Widths)[String(c.codePointAt(0))] ?? 0.55) * size,
    0,
  );
}
const decode = (text: string) =>
  text.replace(
    /&(?:amp|lt|gt|quot|apos|nbsp);|&#(?:x[0-9a-f]+|\d+);/gi,
    (entity) => {
      const names: Record<string, string> = {
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&apos;": "'",
        "&nbsp;": " ",
      };
      if (names[entity]) return names[entity];
      const code = entity.startsWith("&#x")
        ? parseInt(entity.slice(3, -1), 16)
        : parseInt(entity.slice(2, -1));
      return code >= 0 && code <= 0x10ffff ? String.fromCodePoint(code) : "";
    },
  );
export function richTextRuns(html: string): Run[] {
  const safe = html.replace(
    /<script\b[^>]*>[\s\S]*?<\/script\s*>|<style\b[^>]*>[\s\S]*?<\/style\s*>/gi,
    "",
  );
  const tokens = safe.match(/<[^>]*>|[^<]+/g) || [],
    runs: Run[] = [],
    stack: { tag: string; style: Omit<Run, "text"> }[] = [];
  let paragraphIndex = 0;
  for (const token of tokens) {
    if (token[0] !== "<") {
      const style = Object.assign({}, ...stack.map((x) => x.style));
      const featureStyles = stack.filter((entry) => entry.style.textFeatures);
      if (featureStyles.length)
        style.textFeatures = Object.assign(
          {},
          ...featureStyles.map((entry) => entry.style.textFeatures),
        );
      runs.push({ text: decode(token), ...style });
      continue;
    }
    const match = /^<\s*(\/?)\s*([\w]+)([^>]*)>/.exec(token);
    if (!match) continue;
    const [, closing, name, attributes] = match,
      tag = name.toLowerCase();
    if (tag === "br" || tag === "p" || tag === "div" || tag === "li") {
      if (runs.length && !runs.at(-1)!.text.endsWith("\n")) {
        const inherited = Object.assign(
          {},
          ...stack.map((entry) => entry.style),
        );
        runs.push({ text: "\n", ...(inherited.paragraphId ? inherited : {}) });
      }
    }
    if (closing) {
      const index = stack.map((x) => x.tag).lastIndexOf(tag);
      if (index >= 0) stack.splice(index);
      continue;
    }
    const style: Omit<Run, "text"> = {};
    if (tag === "strong" || tag === "b") style.bold = true;
    if (tag === "em" || tag === "i") style.italic = true;
    if (tag === "u") style.underline = true;
    if (tag === "span" || tag === "p") {
      const tab =
        /\bdata-proof-tab-interval\s*=\s*["'](\d+(?:\.\d+)?)["']/i.exec(
          attributes,
        );
      if (tab && Number(tab[1]) >= 0.01 && Number(tab[1]) <= 14400)
        style.tabInterval = Number(tab[1]);
      if (/\bdata-proof-paragraph\s*=\s*["']1["']/i.test(attributes))
        style.paragraphId = `paragraph_${paragraphIndex++}`;
      for (const key of ["spaceBefore", "spaceAfter"] as const) {
        const attr = key === "spaceBefore" ? "before" : "after";
        const spacing = new RegExp(
          `\\bdata-proof-space-${attr}\\s*=\\s*["'](\\d+(?:\\.\\d+)?)["']`,
          "i",
        ).exec(attributes);
        if (spacing && Number(spacing[1]) <= 14400)
          style[key] = Number(spacing[1]);
      }
      const color = /color\s*:\s*(#[a-f\d]{6})/i.exec(attributes);
      if (color) style.color = color[1];
      const size = /font-size\s*:\s*(\d+(?:\.\d+)?)pt/i.exec(attributes);
      if (size) style.size = Math.min(14400, Math.max(1, Number(size[1])));
      const leading =
        /\bdata-proof-leading\s*=\s*["'](\d+(?:\.\d+)?)["']/i.exec(attributes);
      if (leading && Number(leading[1]) > 0 && Number(leading[1]) <= 20)
        style.lineHeight = Number(leading[1]);
      const face = /\bdata-proof-font\s*=\s*["'](font_[a-f0-9]{64})["']/i.exec(
        attributes,
      );
      if (face) style.fontFaceId = face[1];
      for (const key of ["kerning", "ligatures"] as const) {
        const feature = new RegExp(
          `\\bdata-proof-${key}\\s*=\\s*["'](on|off)["']`,
          "i",
        ).exec(attributes);
        if (feature)
          style.textFeatures = {
            ...style.textFeatures,
            [key]: feature[1].toLowerCase() === "on",
          };
      }
      const paint = /\bdata-proof-paint\s*=\s*["']([^"']*)["']/i.exec(
        attributes,
      );
      if (paint) style.sourcePaint = parseImportedPaintAttribute(paint[1]);
    }
    if (["p", "div", "li", "span", "strong", "b", "em", "i", "u"].includes(tag))
      stack.push({ tag, style });
    if (tag === "li") runs.push({ text: "• " });
  }
  if (runs.at(-1)?.text === "\n") runs.pop();
  return runs;
}
export interface TextLine {
  start: number;
  end: number;
  top: number;
  bottom: number;
  baseline: number;
  fontBoxBottom?: number;
  paragraph?: string;
}
export interface TextLayout {
  runs: PositionedRun[];
  lines: TextLine[];
  height: number;
  fontBoxHeight: number;
  ascentShift: number;
}
/** Overhanging ink is intentional when the final baseline fits the frame. */
export function textFrameHeight(
  layout: TextLayout,
  baselineOnly = false,
  fontBox = false,
): number {
  if (fontBox) return layout.fontBoxHeight;
  return baselineOnly ? layout.lines.at(-1)?.baseline || 0 : layout.height;
}
/** Source offsets allow continuation to reflow rich text without dropping its styles. */
export function sliceRuns(runs: Run[], start: number, end = Infinity): Run[] {
  const extents = new Map<string, { start: number; end: number }>();
  let length = 0;
  for (const run of runs) {
    if (run.paragraphId) {
      const previous = extents.get(run.paragraphId);
      extents.set(run.paragraphId, {
        start: previous?.start ?? length,
        end: length + run.text.length,
      });
    }
    length += run.text.length;
  }
  let offset = 0;
  return runs.flatMap((run) => {
    const from = Math.max(0, start - offset),
      to = Math.min(run.text.length, end - offset);
    offset += run.text.length;
    const extent = run.paragraphId ? extents.get(run.paragraphId) : undefined;
    return to > from
      ? [
          {
            ...run,
            text: run.text.slice(from, to),
            ...(extent && start > extent.start && run.spaceBefore !== undefined
              ? { spaceBefore: 0 }
              : {}),
            ...(extent && end < extent.end && run.spaceAfter !== undefined
              ? { spaceAfter: 0 }
              : {}),
          },
        ]
      : [];
  });
}
export function layoutText(
  runs: Run[],
  width: number,
  fontSize: number,
  lineHeight: number,
  family = "Inter",
  weight = 400,
  align: "left" | "center" | "right" = "left",
  context?: FontContext,
  fontBox = false,
): TextLayout {
  const positioned: PositionedRun[] = [],
    lineWidths: number[] = [],
    starts = [0],
    sizes = [fontSize],
    leading = [0],
    ascents = [0],
    descents = [0],
    paragraphs: (string | undefined)[] = [],
    paragraphSpacing = new Map<string, { before: number; after: number }>();
  let x = 0,
    line = 0,
    offset = 0,
    plainParagraph = 0;
  const advance = (start: number) => {
    lineWidths[line] = x;
    line++;
    x = 0;
    starts[line] = start;
    sizes[line] = fontSize;
    leading[line] = 0;
    ascents[line] = 0;
    descents[line] = 0;
  };
  for (const run of runs) {
    if (
      run.tabInterval !== undefined &&
      (!Number.isFinite(run.tabInterval) ||
        run.tabInterval < 0.01 ||
        run.tabInterval > 14400)
    )
      throw new Error("Tab interval must be between 0.01 and 14400 points");
    const paragraph = () => run.paragraphId || `plain_${plainParagraph}`;
    const markParagraph = () => {
      const id = paragraph();
      paragraphs[line] = id;
      const previous = paragraphSpacing.get(id);
      paragraphSpacing.set(id, {
        before: Math.max(previous?.before || 0, run.spaceBefore || 0),
        after: Math.max(previous?.after || 0, run.spaceAfter || 0),
      });
    };
    const explicitFace = run.fontFaceId
      ? context?.manifest.find((face) => face.id === run.fontFaceId)
      : undefined;
    if (run.fontFaceId && !explicitFace)
      throw new Error(
        `Missing managed rich text font snapshot: ${run.fontFaceId}`,
      );
    const variant = context
      ? fontVariant(
          explicitFace ? { ...context, face: explicitFace } : context,
          run.bold,
          run.italic,
        )
      : undefined;
    const runContext =
      context && variant ? { ...context, face: variant.face } : undefined;
    const fontProperties = variant
      ? { fontFaceId: variant.face.id, fontAscent: variant.runtime.ascent }
      : {};
    const size = run.size || fontSize;
    const ascent =
      size *
      (variant?.runtime.ascent ?? fontAscent(family, run.bold ? 700 : weight));
    const descent =
      size *
      (variant
        ? -variant.face.descent
        : fontDescent(family, run.bold ? 700 : weight));
    const add = (text: string, measured: number) => {
      markParagraph();
      positioned.push({
        ...run,
        ...fontProperties,
        text,
        x,
        y: 0,
        width: measured,
        line,
      });
      sizes[line] = Math.max(sizes[line], size);
      leading[line] = Math.max(leading[line], run.lineHeight || lineHeight);
      ascents[line] = Math.max(ascents[line], ascent);
      descents[line] = Math.max(descents[line], descent);
      x += measured;
    };
    for (const token of run.text.split(/(\s+)/)) {
      for (const segment of token.split(
        run.tabInterval === undefined ? /(\n)/ : /(\n|\t)/,
      )) {
        if (!segment) continue;
        if (segment === "\t" && run.tabInterval !== undefined) {
          const next = () =>
            (Math.floor((x + 1e-7) / run.tabInterval!) + 1) * run.tabInterval!;
          if (x && next() > width + 1e-7) advance(offset);
          // Tabs consume source offsets and line metrics but never paint a font glyph.
          add("", next() - x);
          offset++;
          continue;
        }
        if (segment === "\n") {
          markParagraph();
          sizes[line] = Math.max(sizes[line], size);
          leading[line] = Math.max(leading[line], run.lineHeight || lineHeight);
          offset++;
          advance(offset);
          if (!run.paragraphId) plainParagraph++;
          continue;
        }
        const tokenWidth = measureText(
          segment,
          size,
          family,
          run.bold ? 700 : weight,
          runContext,
          run.textFeatures,
        );
        if (tokenWidth > width) {
          for (const c of segment) {
            const measured = measureText(
              c,
              size,
              family,
              run.bold ? 700 : weight,
              runContext,
              run.textFeatures,
            );
            if (x && x + measured > width) advance(offset);
            if (x || !/^\s+$/.test(c)) add(c, measured);
            offset += c.length;
          }
        } else {
          if (x && x + tokenWidth > width) advance(offset);
          if (x || !/^\s+$/.test(segment)) add(segment, tokenWidth);
          offset += segment.length;
        }
      }
    }
  }
  lineWidths[line] = x;
  const before = starts.map(() => 0),
    after = starts.map(() => 0),
    extents = new Map<string, { first: number; last: number }>();
  for (const [index, id] of paragraphs.entries()) {
    if (!id) continue;
    const extent = extents.get(id);
    extents.set(id, { first: extent?.first ?? index, last: index });
  }
  for (const [id, extent] of extents) {
    const spacing = paragraphSpacing.get(id)!;
    before[extent.first] = spacing.before;
    after[extent.last] = spacing.after;
  }
  let height = 0;
  const lines: TextLine[] = starts.map((start, index) => {
    const top = height + before[index],
      baseline = top + Math.max(sizes[index], ascents[index]);
    height =
      top +
      Math.max(
        sizes[index] * (leading[index] || lineHeight),
        (fontBox
          ? ascents[index] || sizes[index] * fontAscent(family, weight, context)
          : baseline - top) + descents[index],
      ) +
      after[index];
    return {
      start,
      end: starts[index + 1] ?? offset,
      top,
      bottom: height,
      baseline,
      paragraph: paragraphs[index] || `plain_${plainParagraph}`,
    };
  });
  const ascentShift =
    lines[0].baseline -
    before[0] -
    (ascents[0] || fontAscent(family, weight, context) * sizes[0]);
  const fontBoxHeight = lines.reduce(
    (height, entry, index) =>
      Math.max(
        height,
        entry.baseline +
          (descents[index] ||
            fontDescent(family, weight, context) * sizes[index]) -
          ascentShift +
          after[index],
      ),
    0,
  );
  if (fontBox)
    for (const [index, entry] of lines.entries())
      entry.fontBoxBottom =
        entry.baseline +
        (descents[index] ||
          fontDescent(family, weight, context) * sizes[index]) -
        ascentShift +
        after[index];
  return {
    lines,
    height,
    fontBoxHeight,
    ascentShift,
    runs: positioned.map((r) => ({
      ...r,
      y: lines[r.line].baseline,
      x:
        r.x +
        (align === "center"
          ? (width - lineWidths[r.line]) / 2
          : align === "right"
            ? width - lineWidths[r.line]
            : 0),
    })),
  };
}
export function layoutRuns(
  runs: Run[],
  width: number,
  fontSize: number,
  lineHeight: number,
  family = "Inter",
  weight = 400,
  align: "left" | "center" | "right" = "left",
  context?: FontContext,
  fontBox = false,
): PositionedRun[] {
  return layoutText(
    runs,
    width,
    fontSize,
    lineHeight,
    family,
    weight,
    align,
    context,
    fontBox,
  ).runs;
}
/** Fits a text frame using the same metrics and styled runs as proof/export. */
export function copyfitFits(
  layout: TextLayout,
  width: number,
  height: number,
  baselineOnly = false,
  fontBox = false,
  maxLinesPerParagraph?: number,
): boolean {
  if (textFrameHeight(layout, baselineOnly, fontBox) > height + 1e-7)
    return false;
  if (
    layout.runs.some((run) => run.x < -1e-7 || run.x + run.width > width + 1e-7)
  )
    return false;
  if (maxLinesPerParagraph) {
    const counts = new Map<string, number>();
    for (const line of layout.lines) {
      const id = line.paragraph || "paragraph";
      const count = (counts.get(id) || 0) + 1;
      if (count > maxLinesPerParagraph) return false;
      counts.set(id, count);
    }
  }
  return true;
}
export function fitText(
  runs: Run[],
  width: number,
  height: number,
  fontSize: number,
  lineHeight: number,
  family = "Inter",
  weight = 400,
  align: "left" | "center" | "right" = "left",
  context?: FontContext,
  baselineOnly = false,
  fontBox = false,
  copyfit?: Copyfit,
  minimumProgress = 0,
) {
  if (copyfit) {
    const atProgress = (progress: number) => {
      const factor = (limit: number) => 1 - progress * (1 - limit),
        fontScale = factor(copyfit.minFontScale),
        leadingScale = factor(copyfit.minLeadingScale),
        beforeScale = factor(copyfit.minSpaceBeforeScale),
        afterScale = factor(copyfit.minSpaceAfterScale),
        size = fontSize * fontScale,
        leading = (lineHeight * leadingScale) / fontScale;
      const scaled =
        progress === 0
          ? runs
          : runs.map((run) => ({
              ...run,
              size: (run.size || fontSize) * fontScale,
              lineHeight:
                ((run.lineHeight || lineHeight) * leadingScale) / fontScale,
              ...(run.spaceBefore === undefined
                ? {}
                : { spaceBefore: run.spaceBefore * beforeScale }),
              ...(run.spaceAfter === undefined
                ? {}
                : { spaceAfter: run.spaceAfter * afterScale }),
            }));
      const layout = layoutText(
        scaled,
        width,
        size,
        leading,
        family,
        weight,
        align,
        context,
        fontBox,
      );
      return {
        fontSize: size,
        lineHeight: leading,
        runs: scaled,
        layout,
        progress,
        fits: copyfitFits(
          layout,
          width,
          height,
          baselineOnly,
          fontBox,
          copyfit.maxLinesPerParagraph,
        ),
      };
    };
    const start = Math.max(0, Math.min(1, minimumProgress)),
      original = atProgress(start);
    if (original.fits) return original;
    const minimum = atProgress(1);
    if (!minimum.fits) return minimum;
    let low = start,
      high = 1;
    for (let i = 0; i < 20; i++) {
      const middle = (low + high) / 2;
      if (atProgress(middle).fits) high = middle;
      else low = middle;
    }
    return atProgress(high);
  }
  const atSize = (size: number) => {
    const scaled = runs.map((r) => ({
      ...r,
      size: ((r.size || fontSize) * size) / fontSize,
    }));
    return {
      runs: scaled,
      layout: layoutText(
        scaled,
        width,
        size,
        lineHeight,
        family,
        weight,
        align,
        context,
        fontBox,
      ),
    };
  };
  let low = Math.min(4, fontSize),
    high = fontSize;
  if (textFrameHeight(atSize(high).layout, baselineOnly, fontBox) <= height)
    return {
      fontSize: high,
      lineHeight,
      progress: 0,
      fits: true,
      ...atSize(high),
    };
  for (let i = 0; i < 12; i++) {
    const middle = (low + high) / 2;
    if (textFrameHeight(atSize(middle).layout, baselineOnly, fontBox) <= height)
      low = middle;
    else high = middle;
  }
  const fitted = atSize(low);
  return {
    fontSize: low,
    lineHeight,
    progress: 0,
    fits: textFrameHeight(fitted.layout, baselineOnly, fontBox) <= height,
    ...fitted,
  };
}
export function wrapText(
  text: string,
  width: number,
  size: number,
  family = "Inter",
  weight = 400,
  context?: FontContext,
): string[] {
  const runs = layoutRuns(
    [{ text }],
    width,
    size,
    1,
    family,
    weight,
    "left",
    context,
  );
  const result: string[] = [];
  for (const run of runs)
    result[run.line] = (result[run.line] || "") + run.text;
  return result.length
    ? Array.from({ length: result.length }, (_, i) => result[i] || "")
    : [""];
}
export function calendarRuns(
  value: string,
  width: number,
  family = "Inter",
  weight = 400,
  context?: FontContext,
  fontSize = 12,
  lineHeight = 1.35,
  align: "left" | "center" | "right" = "center",
): PositionedRun[] {
  const parsed = new Date(`${value.slice(0, 7) || "2026-01"}-01T12:00:00Z`),
    date = Number.isNaN(parsed.getTime())
      ? new Date("2026-01-01T12:00:00Z")
      : parsed,
    days = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
    ).getUTCDate(),
    offset = date.getUTCDay(),
    runs: PositionedRun[] = [];
  const face = context ? fontVariant(context) : undefined;
  const scale = fontSize / 12;
  const rowHeight = Math.max(
    (18 * scale * lineHeight) / 1.35,
    10 *
      scale *
      (Math.max(1, fontAscent(family, weight, context)) +
        fontDescent(family, weight, context)),
  );
  const add = (
    text: string,
    cx: number,
    y: number,
    size: number,
    cellWidth = width,
  ) => {
    size *= scale;
    const measured = measureText(text, size, family, weight, context);
    const padding = Math.min(4, cellWidth / 10);
    runs.push({
      text,
      size,
      x:
        align === "left"
          ? cx - cellWidth / 2 + padding
          : align === "right"
            ? cx + cellWidth / 2 - padding - measured
            : cx - measured / 2,
      y,
      width: measured,
      line: 0,
      ...(face
        ? { fontFaceId: face.face.id, fontAscent: face.runtime.ascent }
        : {}),
    });
  };
  add(
    date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }),
    width / 2,
    18 * scale,
    14,
  );
  ["S", "M", "T", "W", "T", "F", "S"].forEach((day, i) =>
    add(day, ((i + 0.5) * width) / 7, 36 * scale, 10, width / 7),
  );
  for (let day = 1; day <= days; day++) {
    const index = day + offset - 1;
    add(
      String(day),
      (((index % 7) + 0.5) * width) / 7,
      54 * scale + Math.floor(index / 7) * rowHeight,
      10,
      width / 7,
    );
  }
  return runs;
}
export function calendarHeight(
  runs: PositionedRun[],
  family: string,
  weight: number,
  context?: FontContext,
) {
  return Math.max(
    1,
    ...runs.map(
      (r) => r.y + (r.size || 12) * fontDescent(family, weight, context),
    ),
  );
}
export function calendarFitScale(
  runs: PositionedRun[],
  width: number,
  height: number,
  family: string,
  weight: number,
  context?: FontContext,
  align: "left" | "center" | "right" = "center",
) {
  return Math.min(
    1,
    height / calendarHeight(runs, family, weight, context),
    ...runs.map((r, index) => {
      const cell = index === 0 ? width : width / 7,
        usable = align === "center" ? cell : cell - 2 * Math.min(4, cell / 10);
      return r.width ? usable / r.width : 1;
    }),
  );
}
